import type {
  ArenaMeta,
  ArenaState,
  Faction,
  UpgradeId,
  Vec3,
} from "@shared/types";

const API_BASE = "/api";

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({ error: res.statusText }));
  if (!res.ok) {
    throw new Error(json.error || "Request failed");
  }
  return json as T;
}

export async function createArena(matchDurationMs?: number) {
  return request<{ arenaId: string; state: ArenaState }>("POST", "/arena/create", {
    matchDurationMs,
  });
}

export async function joinArena(arenaId: string, name: string, faction: Faction) {
  return request<{
    playerId: string;
    faction: Faction;
    name: string;
    state: ArenaState;
  }>("POST", "/arena/join", { arenaId, name, faction });
}

export async function getArenaState(arenaId: string) {
  return request<{ state: ArenaState }>("GET", `/arena/${arenaId}/state`);
}

export interface Snapshot {
  full: boolean;
  state?: ArenaState;
  tickCount?: number;
  phase?: ArenaState["phase"];
  score?: ArenaState["score"];
  matchEndsAt?: number;
  players?: ArenaState["players"];
  creatures?: ArenaState["creatures"];
  arrows?: ArenaState["arrows"];
  events?: ArenaState["events"];
}

export async function getSnapshot(
  arenaId: string,
  sinceTick: number,
  seen: Record<string, { x: number; z: number; hp: number }>,
): Promise<Snapshot> {
  const params = new URLSearchParams({
    since: String(sinceTick),
    seen: JSON.stringify(seen),
  });
  return request<Snapshot>("GET", `/arena/${arenaId}/snapshot?${params.toString()}`);
}

export async function listArenas() {
  return request<{ arenas: ArenaMeta[] }>("GET", "/arenas");
}

export async function sendMove(arenaId: string, playerId: string, pos: Vec3) {
  return request<{ success: boolean; state: ArenaState; error?: string }>(
    "POST",
    `/arena/${arenaId}/move`,
    { playerId, pos },
  );
}

export async function sendShoot(
  arenaId: string,
  playerId: string,
  target: Vec3,
  targetId?: string,
) {
  return request<{ success: boolean; state: ArenaState; error?: string }>(
    "POST",
    `/arena/${arenaId}/shoot`,
    { playerId, target, targetId },
  );
}

export async function sendUpgrade(
  arenaId: string,
  playerId: string,
  upgrade: UpgradeId,
  arrowType?: string,
) {
  return request<{ success: boolean; state: ArenaState; error?: string }>(
    "POST",
    `/arena/${arenaId}/upgrade`,
    { playerId, upgrade, arrowType },
  );
}
